import { useState, type PointerEvent as ReactPointerEvent } from 'react'
import type { HealthPoint, RatioHealth } from '../../../data/ServerApiClient'
import { Ring } from './charts'
import {
  deltaColor,
  formatDelta,
  gradeChangePoints,
  healthToneColors,
  scoreToGrade,
} from './cfoHealthLabels'
import { CfoHealthCategoryRow } from './CfoHealthCategoryRow'
import { CfoHealthWeightingBadge } from './CfoHealthWeightingBadge'

// The composite-health hero on the CFO ratios page: one 0-100 score + letter grade (A+…F) + this-month
// change, the 24-month health trend with "moved a grade" markers, the AI weighting badge (the
// differentiator), and the five expandable category rows. Renders nothing until there's a score to show
// (a freshly-connected workbook has no ratios yet).

function fmtTrendMonth(period: string): string {
  const d = new Date(`${period.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(d.getTime())
    ? period.slice(0, 7)
    : d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' })
}

function HealthTrend({ series }: { series: HealthPoint[] }) {
  const [hover, setHover] = useState<number | null>(null)
  const values = series.map((p) => p.score)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const W = 680
  const H = 72
  const padTop = 12
  const padBottom = 12
  const padX = 6
  const plotW = W - padX * 2
  const stepX = values.length > 1 ? plotW / (values.length - 1) : 0
  const x = (i: number) => padX + i * stepX
  const y = (v: number) => padTop + (1 - (v - min) / span) * (H - padTop - padBottom)
  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const rising = values[values.length - 1] >= values[0]
  const lineColor = rising ? 'var(--good-acc, #1c8d97)' : 'var(--neg, #c0492f)'
  const changes = gradeChangePoints(series)
  const lastIdx = values.length - 1

  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const frac = (e.clientX - rect.left) / Math.max(rect.width, 1)
    const i = Math.round(frac * Math.max(values.length - 1, 1))
    setHover(Math.min(Math.max(i, 0), values.length - 1))
  }

  const label = (i: number, align: 'start' | 'end') => (
    <div
      style={{
        position: 'absolute',
        [align === 'start' ? 'left' : 'right']: `${(align === 'start' ? x(i) : W - x(i)) / W * 100}%`,
        top: `${(y(values[i]) / H) * 100}%`,
        transform: `translate(${align === 'start' ? '2px' : '-2px'}, -140%)`,
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--brand, #1b4965)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      {align === 'start' ? 'a year ago ' : 'now '}
      {Math.round(values[i])}
    </div>
  )

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        {series.length}-month health trend
      </div>
      <div style={{ position: 'relative', height: H, touchAction: 'none' }} onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Overall financial-health trend, hover for values">
          <polyline points={line} fill="none" stroke={lineColor} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          {changes.map((c) => {
            const i = series.findIndex((p) => p.period === c.period)
            if (i < 0) return null
            return (
              <circle key={c.period} cx={x(i)} cy={y(series[i].score)} r={3.5} fill="var(--surface, #fff)" stroke={c.direction === 'up' ? 'var(--good-acc, #1c8d97)' : 'var(--neg, #c0492f)'} strokeWidth={2}>
                <title>{`${c.direction === 'up' ? 'Up to' : 'Down to'} ${c.grade}`}</title>
              </circle>
            )
          })}
          <circle cx={x(0)} cy={y(values[0])} r={3} fill="var(--surface,#fff)" stroke={lineColor} strokeWidth={2} />
          <circle cx={x(lastIdx)} cy={y(values[lastIdx])} r={4} fill={lineColor} />
          {hover != null ? (
            <>
              <line x1={x(hover)} y1={0} x2={x(hover)} y2={H} stroke="var(--hair-strong, #d4d8de)" strokeWidth={1} strokeDasharray="3 3" />
              <circle cx={x(hover)} cy={y(values[hover])} r={4} fill="var(--brand, #1b4965)" />
            </>
          ) : null}
        </svg>
        {/* begin + end value labels (HTML so text isn't stretched by the non-uniform SVG) */}
        {values.length >= 2 && hover == null ? (
          <>
            {label(0, 'start')}
            {label(lastIdx, 'end')}
          </>
        ) : null}
        {hover != null ? (
          <div
            style={{
              position: 'absolute',
              left: `${(x(hover) / W) * 100}%`,
              top: -2,
              transform: `translateX(${hover > values.length / 2 ? '-100%' : '0'})`,
              background: 'var(--ink, #1b1f24)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              padding: '3px 7px',
              borderRadius: 6,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            {fmtTrendMonth(series[hover].period)} · {Math.round(series[hover].score)}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export type CfoHealthScorePanelProps = {
  health: RatioHealth
  companyName?: string | null
  onSaveWeights?: (weights: Record<string, number>) => Promise<void> | void
  /** Persist a category's per-metric weight override (metric_id → relative weight; 0 excludes). */
  onSaveRatioWeights?: (categoryKey: string, ratioWeights: Record<string, number>) => Promise<void> | void
  /** Persist the per-company "show letter grades" preference. */
  onToggleGrades?: (show: boolean) => Promise<void> | void
  /** Open a sub-metric's full ratio card (by ratio id). */
  onSelectRatio?: (ratioId: string) => void
  busy?: boolean
}

export function CfoHealthScorePanel({ health, companyName, onSaveWeights, onSaveRatioWeights, onToggleGrades, onSelectRatio, busy }: CfoHealthScorePanelProps) {
  const [expanded, setExpanded] = useState<string | null>(null)
  // Optimistic override so the grade toggle updates instantly (the served value is behind the 120s
  // ratios cache; the checkbox stays controlled by the user's intent, not the stale response).
  const [gradesOverride, setGradesOverride] = useState<boolean | null>(null)
  const overall = health.overall ?? null
  if (overall == null) return null

  const showGrades = gradesOverride ?? health.show_health_grades !== false
  const tone = healthToneColors(overall)
  const grade = health.grade ?? scoreToGrade(overall)
  const delta = health.delta_1mo ?? null

  return (
    <section className="hero rise" style={{ marginBottom: 'var(--s4, 16px)' }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <Ring
          value={overall / 100}
          label={Math.round(overall)}
          size={96}
          stroke={9}
          color={tone.color}
          ariaLabel={`Overall financial health ${Math.round(overall)} of 100, grade ${grade}`}
        />
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            Overall financial health
            {companyName ? ` · ${companyName}` : ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 5 }}>
            {showGrades ? (
              <span style={{ fontSize: 15, fontWeight: 800, color: tone.color, background: tone.bg, padding: '2px 9px', borderRadius: 8 }}>
                Grade {grade}
              </span>
            ) : null}
            {delta != null && Math.abs(delta) >= 0.5 ? (
              <span style={{ fontSize: 13, fontWeight: 600, color: deltaColor(delta) }}>
                {delta >= 0 ? '▲' : '▼'} {formatDelta(delta)} this month
              </span>
            ) : null}
          </div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <CfoHealthWeightingBadge weighting={health.weighting} onSaveWeights={onSaveWeights} busy={busy} />
            {onToggleGrades ? (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showGrades}
                  disabled={busy}
                  onChange={(e) => {
                    const next = e.target.checked
                    setGradesOverride(next)
                    void onToggleGrades(next)
                  }}
                />
                Show letter grades
              </label>
            ) : null}
          </div>
        </div>
      </div>

      {health.overall_series.length >= 2 ? <HealthTrend series={health.overall_series} /> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {health.categories.map((category) => (
          <CfoHealthCategoryRow
            key={category.key}
            category={category}
            showGrades={showGrades}
            onSelectRatio={onSelectRatio}
            onSaveRatioWeights={onSaveRatioWeights}
            busy={busy}
            expanded={expanded === category.key}
            onToggle={() => setExpanded(expanded === category.key ? null : category.key)}
          />
        ))}
      </div>

      {health.attention_reason ? (
        <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2, #4c5360)' }}>
          {health.attention_reason}
        </div>
      ) : null}
    </section>
  )
}

export default CfoHealthScorePanel
